/**
 * OcrLexicalEditor — OCR 검수 모달 임베드용 문제은행 Lexical 에디터(구 에디터 이식).
 *
 * 백엔드 규약(ocr-editor-lexical-contract-reply.md): 상세 응답 body_lexical/solution_lexical
 * (editorState JSON 문자열)을 그대로 initialEditorState로 넣고, 저장은 직렬화 문자열을
 * PATCH body_lexical/solution_lexical로 보낸다 — FE에 변환기 없음(승격도 무변환).
 *
 * ProblemEditModal의 탑재 방식을 계승하되 전역 querySelector 스타일 주입 대신
 * App.css의 .ocr-lexical-editor 스코프 규칙을 쓴다(465px 고정폭·높이 하드코딩 제거).
 * dirty 기준은 서버 문자열이 아니라 마운트 직후 재직렬화(정규화) 값 — 파스/직렬화 형식
 * 차이로 무편집이 dirty로 오탐되는 것을 막는다.
 *
 * stateRef: 부모(검수 모달)가 저장 시점에 읽는 통로 — { getState(): string, isDirty(): bool }.
 * ESC는 여기서 소비(전파 중단) — 검수 모달 닫힘/이동과 에디터 조작이 겹치지 않게(이식 계획 0단계).
 */
import React, { useEffect, useRef } from 'react';
import {
  EditorComposer,
  Editor,
  ToolbarPlugin,
  TextFormatDropdown,
  InsertDropdown,
  AlignDropdown,
} from '../../editor';
import Divider from '../../editor/ui/Divider';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
// 본문에서 그림 노드를 빼는 데 쓴다(stateRef.removeImagesBySrc) — 세는 규칙을 계약과 맞추려고
// $dfs 를 쓴다: 계약 §8 의 순회가 「$dfs 가 닿는 type:'image' 전부」다.
import { $dfs, $wrapNodeInElement } from '@lexical/utils';
import {
  $isRootOrShadowRoot, $insertNodes, $createParagraphNode, $getSelection, $isNodeSelection, $isElementNode,
} from 'lexical';
import { $isImageNode, $createImageNode } from '../../editor/nodes/ImageNode';
import FigureEditContext from '../../editor/context/FigureEditContext';
import PasteFigurePlugin from '../../editor/plugins/PasteFigurePlugin';

/**
 * 서버 editorState 값 → LexicalComposer가 안전하게 받는 '문자열'로 정규화.
 *
 * 계약은 문자열이지만 실제로 객체가 올 수 있다(PATCH가 둘 다 허용하는 규약의 대칭). LexicalComposer는
 * typeof === 'object'인 값을 EditorState 인스턴스로 간주해 setEditorState에 그대로 넘기므로,
 * 평범한 JSON 객체가 들어가면 Lexical error #38(빈 editorState)로 트리 전체가 죽는다.
 * 빈 root(children 0개)도 같은 에러를 내므로 여기서 미리 걸러 null을 돌려준다(호출부가 안내로 대체).
 */
export function normalizeEditorState(v) {
  if (v == null) return null;
  let str = null;
  if (typeof v === 'string') {
    str = v.trim() === '' ? null : v;
  } else if (typeof v === 'object') {
    // 계약(문자열)과 다른 형태 — 동작은 시키되 백엔드에 알릴 수 있게 흔적을 남긴다
    // eslint-disable-next-line no-console
    console.warn('[OcrLexicalEditor] body/solution_lexical이 문자열이 아닌 객체로 수신됨 — 문자열 계약 확인 필요');
    try { str = JSON.stringify(v); } catch (_) { return null; }
  }
  if (!str) return null;
  try {
    const o = JSON.parse(str);
    const kids = o?.root?.children;
    if (!Array.isArray(kids) || kids.length === 0) return null; // 빈 문서 → 마운트 금지
  } catch (_) {
    return null; // JSON도 아니면 편집기로 열 수 없다
  }
  return str;
}

/**
 * 에디터 렌더 실패를 이 영역에 가둔다 — 미등록 노드·손상된 state 하나가 검수 모달 전체를
 * 먹통으로 만들지 않게(실제로 error #38이 모달을 죽였다).
 */
class EditorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error('[OcrLexicalEditor] 편집기 렌더 실패', error);
  }

  render() {
    const { error } = this.state;
    const { children, onError } = this.props;
    if (error) return onError(error);
    return children;
  }
}

function Inner({ stateRef, stateKey, baselineState, onDirtyChange, onUnmount, readOnly }) {
  const [editor] = useLexicalComposerContext();
  const baselineRef = useRef(null);
  const dirtyRef = useRef(false);
  // 최신 콜백을 effect 재실행 없이 참조 — cleanup이 낡은 클로저를 부르지 않게
  const onUnmountRef = useRef(onUnmount);
  onUnmountRef.current = onUnmount;
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  useEffect(() => {
    // dirty 기준 = '서버 값(baselineState)' 대비. 스냅샷 복원으로 마운트한 경우에도 기준이 서버라
    // 편집을 서버 내용으로 되돌리면 정확히 dirty가 풀린다. 파스는 정규화 목적(형식 차이 흡수).
    try {
      baselineRef.current = baselineState
        ? JSON.stringify(editor.parseEditorState(baselineState).toJSON())
        : JSON.stringify(editor.getEditorState().toJSON());
    } catch (_) {
      baselineRef.current = JSON.stringify(editor.getEditorState().toJSON());
    }
    if (stateRef) {
      stateRef.current = {
        // 이 편집기가 '어느 문제의 것'인지. 부모가 대조한다 — 문제를 넘긴 직후의 렌더에서는
        // 이전 문제의 편집기가 아직 언마운트되지 않아, key 없이 읽으면 남의 본문이 딸려 온다.
        key: stateKey,
        getState: () => JSON.stringify(editor.getEditorState().toJSON()),
        isDirty: () => JSON.stringify(editor.getEditorState().toJSON()) !== baselineRef.current,
        /**
         * 그 `src` 를 가진 image 노드를 본문에서 뺀다 — **서버가 모르는 그림 전용**이다.
         *
         * 왜 편집기 밖에서 지우는 길이 필요한가: 검수자가 본문에 직접 넣은 이미지는 서버 그림
         * 목록에 없어(계약 §10 · 회신 §5) 삭제 API 로 지울 대상 자체가 없다. 그런데 그 이미지가
         * 남아 있으면 편집본과 원문의 그림 수가 어긋나, 그림을 고칠 때마다 편집본이 폐기된다.
         * 빼는 길이 화면에 없으면 사람이 할 수 있는 일은 [이 문제 다시 OCR] 하나뿐인데
         * 그것은 AI 정답·풀이까지 지운다.
         *
         * **서버가 아는 그림에는 쓰지 않는다.** 그쪽을 이렇게 빼면 서버가 모르는 채로 이력이
         * 어긋난다(회신 §2 Q8 이 금지한 것) — 판정은 부르는 쪽(그림 창)이 서버 목록을 보고 한다.
         *
         * 노드만 담고 있던 문단은 함께 지운다. 빈 문단이 남으면 본문에 설명 없는 공백이 생기고,
         * 사람은 그것을 지우려다 또 저장 관문에 걸린다.
         *
         * @returns 뺀 노드 수(0 이면 그 src 를 못 찾은 것 — 호출부가 그 사실을 말해야 한다)
         */
        removeImagesBySrc: (src) => {
          if (typeof src !== 'string' || src === '') return 0;
          let removed = 0;
          editor.update(() => {
            $dfs().forEach(({ node }) => {
              if (!$isImageNode(node) || node.getSrc() !== src) return;
              const parent = node.getParent();
              node.remove();
              removed += 1;
              // 문단이 이 그림만 담고 있었으면 함께 걷는다(root 직속 노드는 지우지 않는다).
              if (parent && !$isRootOrShadowRoot(parent) && parent.getChildrenSize() === 0) parent.remove();
            });
          });
          return removed;
        },
        /**
         * 이미지를 본문에 **그대로** 넣는다 — 붙여넣기 창에서 「본문에 그냥 넣기」를 고른 경로다.
         *
         * PasteFigurePlugin 이 삽입 명령을 **항상** 가로채기 때문에(창이 비동기라 반환값으로
         * 통과를 정할 수 없다) 넣는 일은 여기로 온다. 하는 일은 ImagesPlugin 의 그것과 같다 —
         * 노드를 만들어 현재 선택 자리에 넣고, root 직속이면 문단으로 감싼다.
         *
         * ⚠ 이렇게 넣은 그림은 **서버가 세지 않는다**(계약 §10 · 회신 §5). 그래서 부르는 쪽이
         * 그 사실을 먼저 말하고 사람이 고른 뒤에만 부른다.
         */
        insertImage: ({ src, altText } = {}) => {
          if (typeof src !== 'string' || src === '') return false;
          editor.update(() => {
            /**
             * 그림이 **선택된 채**면(NodeSelection) `$insertNodes` 가 그 그림을 갈아 끼운다 — 사람은
             * 「넣기」를 골랐지 「바꾸기」를 고른 것이 아니다(바꾸기는 붙여넣기 창의 다른 선택지다).
             * 2026-09-04 실측: 도판을 눌러 둔 채 붙여넣자 도판이 본문에서 사라지고 서버에만 남았다.
             * 선택된 그림 **뒤**로 커서를 옮긴 뒤 넣는다.
             */
            const sel = $getSelection();
            if ($isNodeSelection(sel)) {
              const last = sel.getNodes().slice(-1)[0];
              const top = last?.getTopLevelElement?.() || null;
              if ($isElementNode(top)) top.selectEnd();
              else if (top) { const p = $createParagraphNode(); top.insertAfter(p); p.select(); }
            }
            const node = $createImageNode({ src, altText: altText || '' });
            $insertNodes([node]);
            if ($isRootOrShadowRoot(node.getParentOrThrow())) {
              $wrapNodeInElement(node, $createParagraphNode).selectEnd();
            }
          });
          return true;
        },
      };
    }
    // 마운트 시점의 dirty를 부모에 알린다 — OnChangePlugin은 초기 상태를 알리지 않으므로(ignoreInitialChange),
    // 스냅샷 복원(=dirty)·서버 값 폴백(=clean) 어느 쪽이든 '수정됨' 배지가 실제와 어긋나지 않게 한다.
    const mounted = JSON.stringify(editor.getEditorState().toJSON());
    dirtyRef.current = mounted !== baselineRef.current;
    onDirtyChangeRef.current?.(dirtyRef.current);
    return () => {
      // 언마운트(모드 전환·문제 이동) 직전 스냅샷을 부모에 넘긴다 — 보기/JSON 탭으로 갔다 와도
      // 편집이 사라지지 않게. 부모가 키로 대조해 이 문제의 것만 쓴다.
      const cur = JSON.stringify(editor.getEditorState().toJSON());
      onUnmountRef.current?.({ state: cur, dirty: cur !== baselineRef.current });
      if (stateRef) stateRef.current = null;
    };
  }, [editor, stateRef, stateKey, baselineState]);

  // 편집 가능 여부 재적용 — 공유 Editor.tsx는 마운트 1회만 setEditable하므로(deps []),
  // 저장·재OCR 진행 중(readOnly=true) 입력이 계속 먹히는 것을 여기서 막는다.
  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  const handleChange = (json) => {
    if (baselineRef.current == null) return;
    const d = json !== baselineRef.current;
    if (d !== dirtyRef.current) {
      dirtyRef.current = d;
      onDirtyChange && onDirtyChange(d);
    }
  };

  return (
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
    <div
      className="ocr-lexical-editor"
      onKeyDownCapture={(e) => { if (e.key === 'Escape') e.stopPropagation(); }}
    >
      {/* onChange는 항상 연결 — 편집 차단은 setEditable이 맡는다(dirty 추적이 끊기지 않게) */}
      {/* 자동 포커스 끄기: 검수 모달은 문제를 열 때마다 번호칸에 커서를 주어 ←→·Enter 로 훑는다.
          공용 편집기의 AutoFocusPlugin 은 마운트되자마자 그 커서를 본문으로 가져가는데, 읽기
          전용(보기 탭)에서도 먼저 뺏은 뒤 setEditable(false) 로 커서가 허공에 떨어져 Enter 가
          아무 데도 닿지 않았다. 여기서는 글을 고칠 때 본문을 직접 누르는 쪽이 맞다. */}
      <Editor isEditable={!readOnly} autoFocusEnabled={false} onChange={handleChange}>
        <>
          {/* 이미지가 들어오려는 순간을 호스트에 넘긴다 — FigureEditContext 가 onPasteImage 를
              준 화면에서만 등록된다(플러그인 머리주석). 붙여넣기·드롭·툴바 삽입 셋을 덮는다. */}
          <PasteFigurePlugin />
          {!readOnly ? (
            <ToolbarPlugin>
              <TextFormatDropdown />
              <Divider />
              <InsertDropdown />
              <Divider />
              <AlignDropdown />
            </ToolbarPlugin>
          ) : <span />}
        </>
      </Editor>
    </div>
  );
}

/**
 * initialState: editorState JSON 문자열(body_lexical/solution_lexical). null이면 렌더하지 않는 것이
 * 옳다 — 호출부가 가드한다(계약상 서버가 항상 생성해 주므로 null은 예외 상황).
 */
export default function OcrLexicalEditor({
  initialState, fallbackState, baselineState, label = '본문',
  stateRef, stateKey = null, onDirtyChange, onUnmount, readOnly = false, readOnlyReason = null,
  // 그림 편집 스위치(BE-20260827). { field, onAction } 을 주면 편집기 안 그림에 버튼이 뜬다.
  // **주지 않으면 버튼이 없다** — 이 래퍼가 게이트의 목이다. v1 유물 편집기 6곳과 인쇄
  // 미리보기는 이 래퍼를 안 쓰므로 자동으로 제외된다(editor/context/FigureEditContext.js).
  // readOnly 편집기에는 넘겨도 뜨지 않는다(ImageComponent 가 isEditable 을 함께 본다)만,
  // 호출부가 보기 모드에 아예 주지 않는 쪽이 뜻이 분명하다.
  figureEdit = null,
}) {
  // 어떤 형태로 오든 문자열로 정규화. initialState(복원 스냅샷 등)가 못 여는 값이면 서버 값
  // (fallbackState)으로 폴백한다 — 스냅샷 하나가 깨졌다고 멀쩡한 서버 본문까지 못 열면 안 된다.
  const wanted = normalizeEditorState(initialState);
  const init = wanted ?? normalizeEditorState(fallbackState);
  const base = normalizeEditorState(baselineState) ?? init;
  // 복원하려던 편집분이 열 수 없어 저장본으로 대체된 경우 — 조용히 바뀌면 검수자가 자기 수정이
  // 반영된 줄 알고 승인할 수 있으므로 화면에서 밝힌다.
  const usedFallback = initialState != null && !wanted && !!init;

  if (!init) {
    return (
      <div className="p-3 text-sm text-gray-500">
        편집기로 열 수 있는 {label} 데이터가 없습니다.
        <span className="ml-1 text-gray-400">(내용이 비어 있거나 형식이 올바르지 않습니다 — [JSON] 탭에서 원문을 확인하세요)</span>
      </div>
    );
  }

  return (
    <EditorErrorBoundary
      onError={() => (
        <div className="p-3 text-sm text-danger">
          {label} 편집기를 열지 못했습니다 — 이 문제의 편집 데이터에 지원하지 않는 형식이 섞여 있습니다.
          <span className="ml-1 text-gray-500">[보기]·[JSON] 탭은 그대로 사용할 수 있습니다.</span>
        </div>
      )}
    >
      {usedFallback && (
        <div className="border-b bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
          이전 {label} 편집분을 열 수 없어 저장본을 불러왔습니다 — 다시 수정해 주세요.
        </div>
      )}
      {/* 편집 불가 사유가 있으면 밝힌다 — 툴바가 없는 이유를 화면에서 알 수 있게 */}
      {readOnly && readOnlyReason && (
        <div className="border-b bg-gray-50 px-3 py-1.5 text-xs text-gray-500">{readOnlyReason}</div>
      )}
      {/* Provider 는 EditorComposer 바깥에 둔다 — Lexical 0.11 의 데코레이터(ImageComponent)는
          RichTextPlugin 안에서 돌아 React 트리상 여기 아래에 있고, context 가 그대로 닿는다. */}
      <FigureEditContext.Provider value={figureEdit}>
        <EditorComposer initialEditorState={init}>
          <Inner
            stateRef={stateRef}
            stateKey={stateKey}
            baselineState={base}
            onDirtyChange={onDirtyChange}
            onUnmount={onUnmount}
            readOnly={readOnly}
          />
        </EditorComposer>
      </FigureEditContext.Provider>
    </EditorErrorBoundary>
  );
}
